<!--
// app/components/pages/mercato/GlobalMercatoSimulator.vue
// @component GlobalMercatoSimulator - Full 3-step modal for mercato squad simulation
-->
<script setup lang="ts">
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoPlayer } from "~~/src/mercato/domain/models/MercatoPlayer";
import MercatoSquadTab from "./simulator/MercatoSquadTab.vue";
import MercatoMarketTab from "./simulator/MercatoMarketTab.vue";
import MercatoSquadSummary from "./simulator/MercatoSquadSummary.vue";
import LeftArrow from "~/components/Shared/icons/LeftArrow.vue";
import ResetIcon from "~/components/Shared/icons/ResetIcon.vue";

type Props = {
  teams: MercatoTeam[];
  league: string | string[];
  marketPlayers: MercatoPlayer[] | null | undefined;
};

const props = defineProps<Props>();

const isOpen = defineModel<boolean>({ required: true });

const { $t } = useI18n();

// State management
const currentStep = ref<1 | 2 | 3>(1);
const selectedTeam = ref<MercatoTeam | null>(null);
const currentSquad = ref<MercatoPlayer[]>([]);
const initialSquad = ref<MercatoPlayer[]>([]);
const activeTab = ref<"squad" | "market">("squad");
const showResetConfirmation = ref(false);

// Constants
const MAX_PLAYERS = 12;
const MIN_GOALKEEPERS = 1;

// Build team players map reactively from props
const teamPlayersMap = computed(() => {
  if (!props.marketPlayers || props.marketPlayers.length === 0) {
    return new Map<number, MercatoPlayer[]>();
  }

  const map = new Map<number, MercatoPlayer[]>();
  props.marketPlayers.forEach(player => {
    const teamId = parseInt(String(player.teamId));
    if (!map.has(teamId)) {
      map.set(teamId, []);
    }
    map.get(teamId)!.push(player);
  });

  return map;
});

// Check for errors loading players
const playersLoadError = computed(() => {
  if (!props.marketPlayers || props.marketPlayers.length === 0) {
    return $t("mercatoSimulator.error.loadPlayers");
  }
  return null;
});

// Filter teams by league
const filteredTeams = computed(() => {
  const leagueValue = Array.isArray(props.league) ? props.league[0] : props.league;
  if (leagueValue === "all") {
    return props.teams.filter(team => team.id !== 0);
  }
  return props.teams.filter(team => team.league === leagueValue && team.id !== 0);
});

const getTeamPlayerCount = (teamId: number): number => {
  return teamPlayersMap.value.get(teamId)?.length || 0;
};

const getTeamPlayers = (teamId: number): MercatoPlayer[] => {
  return teamPlayersMap.value.get(teamId) || [];
};

// Validation
const isSquadFull = computed(() => currentSquad.value.length >= MAX_PLAYERS);

const goalkeepersCount = computed(() => {
  return currentSquad.value.filter(p => p.position === "goalkeeper").length;
});

const canProceedToStep3 = computed(() => {
  return (
    currentSquad.value.length > 0 &&
    currentSquad.value.length === MAX_PLAYERS &&
    goalkeepersCount.value >= MIN_GOALKEEPERS
  );
});

const validationMessage = computed(() => {
  if (goalkeepersCount.value < MIN_GOALKEEPERS) {
    return $t("mercatoSimulator.validation.minGoalkeepers");
  }
  return "";
});

// Step 1: Team selection
const handleTeamSelect = (team: MercatoTeam) => {
  selectedTeam.value = team;
  const teamPlayers = getTeamPlayers(team.id);
  currentSquad.value = [...teamPlayers];
  initialSquad.value = [...teamPlayers];
  currentStep.value = 2;
  activeTab.value = "squad";
};

// Step 2: Buy/Sell players
const handlePlayerBuy = (player: MercatoPlayer) => {
  if (isSquadFull.value) return;
  if (currentSquad.value.some(p => p.id === player.id)) return;

  currentSquad.value.push(player);
};

const handlePlayerSell = (player: MercatoPlayer) => {
  currentSquad.value = currentSquad.value.filter(p => p.id !== player.id);
};

// Navigation
const nextStep = () => {
  if (currentStep.value === 2 && canProceedToStep3.value) {
    currentStep.value = 3;
  }
};

const prevStep = () => {
  if (currentStep.value === 3) {
    currentStep.value = 2;
  } else if (currentStep.value === 2) {
    currentStep.value = 1;
    selectedTeam.value = null;
    currentSquad.value = [];
    initialSquad.value = [];
  }
};

// Reset
const handleReset = () => {
  showResetConfirmation.value = true;
};

const confirmReset = () => {
  currentStep.value = 1;
  selectedTeam.value = null;
  currentSquad.value = [];
  initialSquad.value = [];
  activeTab.value = "squad";
  showResetConfirmation.value = false;
};

// Close modal
const handleClose = () => {
  isOpen.value = false;
  // Reset after animation completes
  setTimeout(() => {
    currentStep.value = 1;
    selectedTeam.value = null;
    currentSquad.value = [];
    initialSquad.value = [];
    activeTab.value = "squad";
  }, 300);
};

// Load dom-to-image script
useHead({
  script: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/dom-to-image/2.6.0/dom-to-image.min.js",
      tagPosition: "head",
      tagPriority: "high",
    },
  ],
});
</script>

<template>
  <div
    v-if="isOpen"
    @close="handleClose"
    :aria-label="$t('mercatoSimulator.title')"
    class="w-full h-full kql-w-container py-4 px-2 md:px-0"
  >
    <div>
      <div class="simulator-header">
        <!-- Title & Step Indicator -->
        <div
          class="simulator-header__title min-w-full bigsm:min-w-max bigsm:max-w-max bigsm:order-2 bigsm:mx-auto"
        >
          <h2 class="text-2xl font-bold">{{ $t("mercatoSimulator.title") }}</h2>
          <p class="text-sm text-gray-400">
            <span class="bg-mercato p-1 rounded text-black me-1"
              ><span class="font-bold">{{ currentStep }}</span> / 3</span
            >
            <span v-if="currentStep === 1"> {{ $t("mercatoSimulator.step1.title") }}</span>
            <span v-else-if="currentStep === 2"> {{ $t("mercatoSimulator.step2.title") }}</span>
            <span v-else> {{ $t("mercatoSimulator.step3.title") }}</span>
          </p>
        </div>

        <!-- Back Button (Steps 2 & 3) -->
        <button
          v-if="currentStep > 1"
          type="button"
          class="btn-back bigsm:order-first"
          @click="prevStep"
          :aria-label="$t('mercatoSimulator.button.back')"
        >
          <LeftArrow class="w-5 h-5" />
          <span>{{ $t("mercatoSimulator.button.back") }}</span>
        </button>

        <!-- Reset Button -->
        <button
          v-if="currentStep > 1"
          type="button"
          class="btn-reset bigsm:order-last"
          @click="handleReset"
          :aria-label="$t('mercatoSimulator.button.reset')"
        >
          <ResetIcon class="w-5 h-5" />
          <span>{{ $t("mercatoSimulator.button.reset") }}</span>
        </button>

        <div class="absolute top-0 ltr:right-0 rtl:left-0 bigsm:static bigsm:order-last">
          <button
            type="button"
            class="text-white bg-gray-700 hover:bg-primary hover:text-black rounded-lg text-sm w-7 h-7 ms-auto inline-flex justify-center items-center"
            @click="handleClose"
          >
            <svg
              class="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span class="sr-only">Close mercato simulator </span>
          </button>
        </div>
      </div>
    </div>

    <div>
      <div class="simulator-body">
        <!-- Step 1: Team Selection -->
        <div v-if="currentStep === 1" class="step-content">
          <div class="teams-grid">
            <lazy-pages-mercato-simulator-mercato-team-card
              v-for="team in filteredTeams"
              :key="team.id"
              :team="team"
              :player-count="getTeamPlayerCount(team.id)"
              :is-selected="selectedTeam?.id === team.id"
              @select="handleTeamSelect"
            />
          </div>
        </div>

        <!-- Step 2: Squad Management -->
        <div v-else-if="currentStep === 2" class="step-content">
          <!-- Error UI for Player Load Failures -->
          <div
            v-if="playersLoadError"
            class="mb-6 rounded-lg bg-red-900 bg-opacity-20 border border-red-500 p-4 text-center"
          >
            <p class="text-red-400">{{ playersLoadError }}</p>
          </div>

          <!-- Tab Navigation -->
          <div class="tabs-navigation">
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'squad' }]"
              @click="activeTab = 'squad'"
            >
              {{ $t("mercatoSimulator.tab.squad") }}
            </button>
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'market' }]"
              @click="activeTab = 'market'"
            >
              {{ $t("mercatoSimulator.tab.market") }}
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tabs-content">
            <MercatoSquadTab
              v-if="activeTab === 'squad'"
              :team="selectedTeam!"
              :squad="currentSquad"
              :max-players="MAX_PLAYERS"
              @sell-player="handlePlayerSell"
            />
            <MercatoMarketTab
              v-else
              :players="props.marketPlayers"
              :current-squad="currentSquad"
              :is-squad-full="isSquadFull"
              @buy-player="handlePlayerBuy"
            />
          </div>

          <!-- Validation & Next Button -->
          <div class="step-actions">
            <div class="validation-message">
              <p v-if="validationMessage" class="text-red-500 text-sm">{{ validationMessage }}</p>
            </div>
            <button type="button" class="btn-next" :disabled="!canProceedToStep3" @click="nextStep">
              {{ $t("mercatoSimulator.button.next") }}
            </button>
          </div>
        </div>

        <!-- Step 3: Summary -->
        <div v-else-if="currentStep === 3" class="step-content">
          <MercatoSquadSummary :team="selectedTeam!" :squad="currentSquad" />
        </div>
      </div>
    </div>

    <!-- Reset Confirmation Dialog -->
    <div
      v-if="showResetConfirmation"
      class="fixed inset-0 bottom-0 right-0 left-0 z-10 flex items-center justify-center bg-black bg-opacity-75"
    >
      <div class="max-w-md rounded-lg bg-gray-900 p-6 text-center shadow-xl border-2 border-mercato">
        <p class="mb-4 text-lg font-bold text-white">
          {{ $t("mercatoSimulator.confirmReset") }}
        </p>
        <div class="flex gap-4">
          <button
            @click="showResetConfirmation = false"
            class="flex-1 rounded-lg bg-gray-700 px-6 py-3 font-bold text-white transition-colors hover:bg-gray-600"
          >
            {{ $t("squadSimulator.button.cancel") }}
          </button>
          <button
            @click="confirmReset"
            class="flex-1 rounded-lg bg-red-600 px-6 py-3 font-bold text-white transition-colors hover:bg-red-700"
          >
            {{ $t("squadSimulator.button.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.simulator-header {
  @apply flex items-center flex-wrap justify-between gap-4 pb-4 border-b border-gray-700 relative;
}

.simulator-header__title {
  @apply flex-1 text-center;
}

.btn-back,
.btn-reset {
  @apply flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors text-sm font-semibold;
}

.simulator-body {
  @apply mt-6;
}

.step-content {
  @apply flex flex-col gap-6;
}

/* Step 1: Teams Grid */
.teams-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] px-2 overflow-y-auto overflow-x-hidden;
}

/* Step 2: Tabs */
.tabs-navigation {
  @apply flex border-b border-gray-700 gap-2;
}

.tab-button {
  @apply flex-1 py-3 px-4 text-sm font-semibold uppercase transition-all duration-200 border-b-2 border-transparent;
}

.tab-button.active {
  @apply border-mercato text-mercato;
}

.tab-button:not(.active) {
  @apply text-gray-400 hover:text-gray-200;
}

.tabs-content {
  @apply mt-4;
}

.step-actions {
  @apply flex items-center justify-between mt-6 pt-6 border-t border-gray-700;
}

.validation-message {
  @apply flex-1;
}

.btn-next {
  @apply bg-mercato text-black px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
